import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { riskManagerService } from '../../services/riskManagerService';
import { RiskProfile, RiskSource, RiskLevel, MitigationStatus } from '../../types/risk';
// Using Tailwind CSS for icons instead of React icons
import toast from 'react-hot-toast';

const RiskManager: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [riskProfiles, setRiskProfiles] = useState<RiskProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [riskSummary, setRiskSummary] = useState<any>(null);
  
  const [currentProfile, setCurrentProfile] = useState<RiskProfile>({
    title: '',
    description: '',
    source: RiskSource.INTERNAL_OPERATIONAL,
    level: RiskLevel.MEDIUM,
    potential_impact: 5,
    probability_of_occurrence: 5,
    mitigation_plan: '',
    mitigation_status: MitigationStatus.NOT_STARTED,
    mitigation_deadline: ''
  });
  
  // State for AI-generated mitigation strategies
  const [mitigationStrategies, setMitigationStrategies] = useState<string[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);

  const [filters, setFilters] = useState({
    source: '',
    level: '',
    mitigationStatus: '',
    minRiskScore: '',
    maxRiskScore: '',
    sortBy: 'risk_score',
    sortDirection: 'desc'
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadRiskProfiles();
    loadRiskSummary();
  }, [user, navigate]);

  const loadRiskProfiles = async () => {
    try {
      setLoading(true);
      const data = await riskManagerService.getAll();
      setRiskProfiles(data);
    } catch (error) {
      console.error('Error loading risk profiles:', error);
      toast.error('Failed to load risk profiles');
    } finally {
      setLoading(false);
    }
  };

  const loadRiskSummary = async () => {
    try {
      const data = await riskManagerService.getSummary();
      setRiskSummary(data);
    } catch (error) {
      console.error('Error loading risk summary:', error);
      toast.error('Failed to load risk summary');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    // Parse numeric values
    if (['potential_impact', 'probability_of_occurrence'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentProfile(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate user is logged in
    if (!user || !user.id) {
      toast.error('You must be logged in to save risk profiles');
      return;
    }
    
    try {
      // Ensure user_id AND userId are both included for maximum compatibility
      const profileToSave = {
        ...currentProfile,
        user_id: user.id,
        userId: user.id // Include both formats to ensure backend compatibility
      };
      
      console.log('Saving risk profile with data:', profileToSave);
      
      if (currentProfile.id) {
        await riskManagerService.update(currentProfile.id, profileToSave);
        toast.success('Risk profile updated successfully');
      } else {
        await riskManagerService.create(profileToSave);
        toast.success('Risk profile created successfully');
      }
      
      setShowForm(false);
      resetForm();
      loadRiskProfiles();
      loadRiskSummary();
    } catch (error: any) {
      console.error('Error saving risk profile:', error);
      // Provide more specific error message if available
      const errorMessage = error?.message || 'Failed to save risk profile';
      toast.error(`Error: ${errorMessage}`);
    }
  };

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      const data = await riskManagerService.getFiltered(filters);
      setRiskProfiles(data);
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    setFilters({
      source: '',
      level: '',
      mitigationStatus: '',
      minRiskScore: '',
      maxRiskScore: '',
      sortBy: 'risk_score',
      sortDirection: 'desc'
    });
    loadRiskProfiles();
  };

  const handleEdit = (profile: RiskProfile) => {
    setCurrentProfile(profile);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this risk profile?')) {
      try {
        await riskManagerService.delete(id);
        toast.success('Risk profile deleted successfully');
        loadRiskProfiles();
        loadRiskSummary();
      } catch (error) {
        console.error('Error deleting risk profile:', error);
        toast.error('Failed to delete risk profile');
      }
    }
  };

  const resetForm = () => {
    setCurrentProfile({
      title: '',
      description: '',
      source: RiskSource.INTERNAL_OPERATIONAL,
      level: RiskLevel.MEDIUM,
      potential_impact: 5,
      probability_of_occurrence: 5,
      mitigation_plan: '',
      mitigation_status: MitigationStatus.NOT_STARTED,
      mitigation_deadline: ''
    });
    setMitigationStrategies([]);
  };
  
  // Handle generation of AI mitigation strategies
  const handleGenerateStrategies = async () => {
    if (!currentProfile.title || !currentProfile.description) {
      toast.error('Title and description are required to generate strategies');
      return;
    }
    
    // Validate user is logged in
    if (!user || !user.id) {
      toast.error('You must be logged in to generate mitigation strategies');
      return;
    }
    
    try {
      setLoadingStrategies(true);
      const riskDetails = {
        title: currentProfile.title,
        description: currentProfile.description,
        source: currentProfile.source,
        level: currentProfile.level,
        impactDescription: currentProfile.description, // Use description as impact description
        probabilityScore: currentProfile.probability_of_occurrence,
        impactScore: currentProfile.potential_impact,
        riskScore: currentProfile.potential_impact * currentProfile.probability_of_occurrence / 10,
        userId: user.id // Include userId for authentication
      };
      
      console.log('Generating mitigation strategies with data:', riskDetails);
      
      const response = await riskManagerService.generateMitigationStrategies(riskDetails);
      setMitigationStrategies(response.strategies);
      toast.success('AI mitigation strategies generated');
    } catch (error: any) {
      console.error('Error generating mitigation strategies:', error);
      // Provide more specific error message if available
      const errorMessage = error?.message || 'Failed to generate mitigation strategies';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setLoadingStrategies(false);
    }
  };
  
  // Apply a selected strategy to the mitigation plan
  const handleApplyStrategy = (strategy: string) => {
    const currentPlan = currentProfile.mitigation_plan || '';
    const updatedPlan = currentPlan ? `${currentPlan}\n\n${strategy}` : strategy;
    
    setCurrentProfile(prev => ({
      ...prev,
      mitigation_plan: updatedPlan,
      mitigation_status: prev.mitigation_status === MitigationStatus.NOT_STARTED ? 
                        MitigationStatus.IN_PROGRESS : prev.mitigation_status
    }));
    
    toast.success('Strategy applied to mitigation plan');
  };

  // Calculate risk score color based on the score
  const getRiskScoreColor = (score: number): string => {
    if (score >= 8) return 'text-red-600';
    if (score >= 6) return 'text-orange-500';
    if (score >= 4) return 'text-yellow-500';
    if (score >= 2) return 'text-green-500';
    return 'text-green-700';
  };

  if (loading && riskProfiles.length === 0) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-12 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')] bg-[size:50px_50px]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        
        <div className="mb-6">
          <button
            onClick={() => navigate('/finance')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow px-3 py-1 rounded-lg"
          >
            <i className="fas fa-arrow-left mr-2"></i> Back to Financial Tools
          </button>
        </div>
        
        <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
          <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">Risk Manager</h1>
          <div className="h-1 w-32 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          <p className="text-gray-700 mb-6">
            Identify, assess, and mitigate business risks to ensure the long-term success of your business.
          </p>
          
          {riskSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white/90 p-4 rounded-lg shadow-md border border-gray-100">
                <div className="font-bold text-gray-700">Total Risks</div>
                <div className="text-3xl font-bold">{riskSummary.total_risks}</div>
              </div>
              <div className="bg-white/90 p-4 rounded-lg shadow-md border border-gray-100">
                <div className="font-bold text-red-600">Critical Risks</div>
                <div className="text-3xl font-bold text-red-600">{riskSummary.critical_risks}</div>
              </div>
              <div className="bg-white/90 p-4 rounded-lg shadow-md border border-gray-100">
                <div className="font-bold text-orange-500">High Risks</div>
                <div className="text-3xl font-bold text-orange-500">{riskSummary.high_risks}</div>
              </div>
              <div className="bg-white/90 p-4 rounded-lg shadow-md border border-gray-100">
                <div className="font-bold text-yellow-500">Medium Risks</div>
                <div className="text-3xl font-bold text-yellow-500">{riskSummary.medium_risks}</div>
              </div>
              <div className="bg-white/90 p-4 rounded-lg shadow-md border border-gray-100">
                <div className="font-bold text-green-600">Low Risks</div>
                <div className="text-3xl font-bold text-green-600">{riskSummary.low_risks}</div>
              </div>
            </div>
          )}
        </div>

        {!showForm ? (
          <div className="mb-6">
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-0.5 flex items-center"
            >
              <i className="fas fa-plus mr-2"></i> Create New Risk Assessment
            </button>
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6 mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
            <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              {currentProfile.id ? 'Edit Risk Profile' : 'Create New Risk Profile'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={currentProfile.title}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    placeholder="e.g., Supply Chain Disruption"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Source
                  </label>
                  <select
                    name="source"
                    value={currentProfile.source}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                  >
                    <option value={RiskSource.INTERNAL_OPERATIONAL}>{RiskSource.INTERNAL_OPERATIONAL}</option>
                    <option value={RiskSource.INTERNAL_FINANCIAL}>{RiskSource.INTERNAL_FINANCIAL}</option>
                    <option value={RiskSource.EXTERNAL_MARKET}>{RiskSource.EXTERNAL_MARKET}</option>
                    <option value={RiskSource.EXTERNAL_REGULATORY}>{RiskSource.EXTERNAL_REGULATORY}</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={currentProfile.description}
                  onChange={handleInputChange}
                  className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  placeholder="Describe the risk in detail"
                  rows={3}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Level
                  </label>
                  <select
                    name="level"
                    value={currentProfile.level}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                  >
                    <option value={RiskLevel.CRITICAL}>{RiskLevel.CRITICAL}</option>
                    <option value={RiskLevel.HIGH}>{RiskLevel.HIGH}</option>
                    <option value={RiskLevel.MEDIUM}>{RiskLevel.MEDIUM}</option>
                    <option value={RiskLevel.LOW}>{RiskLevel.LOW}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Potential Impact (0-10)
                  </label>
                  <input
                    type="number"
                    name="potential_impact"
                    value={currentProfile.potential_impact}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probability (0-10)
                  </label>
                  <input
                    type="number"
                    name="probability_of_occurrence"
                    value={currentProfile.probability_of_occurrence}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Mitigation Plan
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateStrategies}
                    disabled={loadingStrategies || !currentProfile.title || !currentProfile.description}
                    className={`px-2 py-1 text-xs rounded-md flex items-center ${loadingStrategies ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white transition-colors duration-300`}
                  >
                    {loadingStrategies ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a.75.75 0 000 1.5h4.59l-2.1 1.95a.75.75 0 001.02 1.1l3.5-3.25a.75.75 0 000-1.1l-3.5-3.25a.75.75 0 10-1.02 1.1l2.1 1.95H6.75z" clipRule="evenodd" />
                        </svg>
                        Generate AI Strategies
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  name="mitigation_plan"
                  value={currentProfile.mitigation_plan || ''}
                  onChange={handleInputChange}
                  className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  placeholder="What steps will you take to address this risk?"
                  rows={3}
                />
                
                {/* Display AI-generated strategies */}
                {mitigationStrategies.length > 0 && (
                  <div className="mt-2 bg-blue-50/80 backdrop-blur-sm rounded-lg p-3 border border-blue-200">
                    <h5 className="text-sm font-semibold text-blue-800 mb-2">AI-Suggested Mitigation Strategies:</h5>
                    <ul className="space-y-2">
                      {mitigationStrategies.map((strategy, index) => (
                        <li key={index} className="text-sm">
                          <button 
                            type="button"
                            onClick={() => handleApplyStrategy(strategy)}
                            className="flex group w-full text-left"
                          >
                            <span className="flex-shrink-0 w-5 h-5 mr-1.5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 group-hover:bg-blue-200">
                              {index + 1}
                            </span>
                            <span className="text-gray-700 group-hover:text-blue-700">{strategy}</span>
                            <span className="ml-auto invisible group-hover:visible text-blue-500 text-xs">
                              Apply
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mitigation Status
                  </label>
                  <select
                    name="mitigation_status"
                    value={currentProfile.mitigation_status || MitigationStatus.NOT_STARTED}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value={MitigationStatus.NOT_STARTED}>{MitigationStatus.NOT_STARTED}</option>
                    <option value={MitigationStatus.IN_PROGRESS}>{MitigationStatus.IN_PROGRESS}</option>
                    <option value={MitigationStatus.COMPLETED}>{MitigationStatus.COMPLETED}</option>
                    <option value={MitigationStatus.ONGOING}>{MitigationStatus.ONGOING}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Completion Date
                  </label>
                  <input
                    type="date"
                    name="mitigation_deadline"
                    value={currentProfile.mitigation_deadline || ''}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <i className="fas fa-save mr-2"></i> {currentProfile.id ? 'Update' : 'Save'} Risk Profile
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Risk profiles list */}
        {!showForm && riskProfiles.length > 0 && (
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Your Risk Profiles
              </h2>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <i className="fas fa-filter mr-1"></i> {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>
            
            {showFilters && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Risk Source</label>
                    <select
                      name="source"
                      value={filters.source}
                      onChange={handleFilterChange}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">All Sources</option>
                      <option value={RiskSource.INTERNAL_OPERATIONAL}>{RiskSource.INTERNAL_OPERATIONAL}</option>
                      <option value={RiskSource.INTERNAL_FINANCIAL}>{RiskSource.INTERNAL_FINANCIAL}</option>
                      <option value={RiskSource.EXTERNAL_MARKET}>{RiskSource.EXTERNAL_MARKET}</option>
                      <option value={RiskSource.EXTERNAL_REGULATORY}>{RiskSource.EXTERNAL_REGULATORY}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                    <select
                      name="level"
                      value={filters.level}
                      onChange={handleFilterChange}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">All Levels</option>
                      <option value={RiskLevel.CRITICAL}>{RiskLevel.CRITICAL}</option>
                      <option value={RiskLevel.HIGH}>{RiskLevel.HIGH}</option>
                      <option value={RiskLevel.MEDIUM}>{RiskLevel.MEDIUM}</option>
                      <option value={RiskLevel.LOW}>{RiskLevel.LOW}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                    <select
                      name="sortBy"
                      value={filters.sortBy}
                      onChange={handleFilterChange}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="risk_score">Risk Score</option>
                      <option value="created_at">Date Created</option>
                      <option value="updated_at">Date Updated</option>
                      <option value="mitigation_deadline">Mitigation Deadline</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={resetFilters}
                    className="px-3 py-1 text-sm rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleApplyFilters}
                    className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {riskProfiles.map((profile) => (
                    <tr key={profile.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {profile.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {profile.source}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          profile.level === RiskLevel.CRITICAL ? 'bg-red-100 text-red-800' :
                          profile.level === RiskLevel.HIGH ? 'bg-orange-100 text-orange-800' :
                          profile.level === RiskLevel.MEDIUM ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {profile.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={getRiskScoreColor(profile.risk_score || 0)}>
                          {profile.risk_score?.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {profile.mitigation_status || 'Not Started'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(profile)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(profile.id!)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {riskProfiles.length === 0 && (
              <div className="text-center py-10">
                <i className="fas fa-exclamation-circle text-gray-400 text-3xl mb-3"></i>
                <p className="text-gray-500">No risk profiles found. Create your first risk assessment!</p>
              </div>
            )}
          </div>
        )}
        
        {!showForm && riskProfiles.length === 0 && (
          <div className="text-center py-16 bg-white/20 backdrop-blur-lg rounded-xl border border-white/30 shadow-lg">
            <div className="text-4xl text-gray-300 mb-4">
              <i className="fas fa-shield"></i>
            </div>
            <h3 className="text-xl font-semibold mb-2">No Risk Profiles Yet</h3>
            <p className="text-gray-500 mb-6">Create your first risk assessment to start managing potential threats to your business.</p>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-0.5 flex items-center mx-auto"
            >
              <i className="fas fa-plus mr-2"></i> Create First Risk Assessment
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RiskManager;
